import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, ArrowRight } from "lucide-react";
import { API, scrollToTop, setCanonical, STATE_NAMES_MAP } from "../lib/config";
import { Nav, Footer, HeroBanner, CookieConsent } from "../components/layout";
import { ChatBot } from "../components/chat";
import { AffiliateBanner, AdSensePlaceholder, MoneyStrip } from "../components/ads";

export default function FindYourCity() {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [lookupCity, setLookupCity] = useState("");
  const [lookupState, setLookupState] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setCanonical("/find-your-city");
    document.title = "Find Your City | Auto Insurance Guide | QuotesByCity";
    axios.get(`${API}/cities?limit=10000`).then(r => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  const filtered = cities.filter(c => {
    const matchSearch = !search || c.city.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search.toLowerCase());
    const matchState = !stateFilter || c.state === stateFilter;
    return matchSearch && matchState;
  }).slice(0, 24);

  const states = [...new Set(cities.map(c => c.state))].sort();

  const handleLookup = async () => {
    if (!lookupCity || !lookupState) return;
    setLookupLoading(true);
    try {
      const r = await axios.post(`${API}/city-lookup`, { city: lookupCity, state: lookupState });
      if (r.data.city) {
        navigate(`/quotes/${r.data.city.slug}`);
      }
    } catch {
      setLookupResult("error");
    }
    setLookupLoading(false);
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <Nav />
      <HeroBanner title="Auto Insurance Guide for Every US City" subtitle="Find your city below, or type any city for an instant guide" badge="All US Cities" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* MAIN CONTENT */}
          <div className="flex-1 min-w-0 space-y-8">
            {/* SEARCH */}
            <section className="bg-white rounded-xl border border-slate-200 p-6" data-testid="city-search">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Type your city name..." className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none" data-testid="city-search-input" />
                </div>
                <select value={stateFilter} onChange={e => setStateFilter(e.target.value)} className="px-4 py-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-sky-500 outline-none bg-white" data-testid="state-filter">
                  <option value="">All States</option>
                  {states.map(s => <option key={s} value={s}>{STATE_NAMES_MAP[s] || s}</option>)}
                </select>
              </div>
              <p className="text-xs text-slate-400 mt-2">Showing {filtered.length} of {cities.length} cities.</p>
            </section>

            {/* CITY GRID */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="city-grid">
              {filtered.map(c => (
                <Link key={c.slug} to={`/quotes/${c.slug}`} onClick={scrollToTop} className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 hover:text-sky-700 hover:border-sky-300 transition-all flex items-center gap-1.5 font-medium" data-testid={`city-${c.slug}`}>
                  <MapPin size={12} className="text-slate-400 shrink-0" /> {c.city}, {c.state}
                </Link>
              ))}
            </div>

            {/* MOBILE ADS - shown between content on mobile */}
            <div className="lg:hidden">
              <MoneyStrip />
            </div>

            {/* CAN'T FIND YOUR CITY */}
            <section className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center" data-testid="city-lookup">
              <MapPin size={28} className="text-sky-700 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Can't Find Your City?</h2>
              <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">No problem! Enter any US city and state below and we'll create a personalized auto insurance guide for you instantly.</p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input value={lookupCity} onChange={e => setLookupCity(e.target.value)} placeholder="Enter your city..." className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-sky-500 outline-none" data-testid="lookup-city-input" />
                <select value={lookupState} onChange={e => setLookupState(e.target.value)} className="px-4 py-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-sky-500 outline-none bg-white" data-testid="lookup-state-select">
                  <option value="">State</option>
                  {Object.entries(STATE_NAMES_MAP).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
                <button onClick={handleLookup} disabled={lookupLoading || !lookupCity || !lookupState} className="bg-sky-700 text-white rounded-xl px-6 py-3 text-sm font-medium hover:bg-sky-800 disabled:opacity-50 transition-all flex items-center gap-2 justify-center" data-testid="lookup-submit">
                  {lookupLoading ? "Creating..." : <>Look Up <Search size={14} /></>}
                </button>
              </div>
              {lookupResult === "error" && <p className="text-sm text-red-500 mt-3">Something went wrong. Please try again.</p>}
            </section>
          </div>

          {/* SIDEBAR - Desktop */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6 hidden lg:block" data-testid="sidebar">
            <div className="lg:sticky lg:top-20 space-y-6">
              <AffiliateBanner />
              <AdSensePlaceholder size="large" />
            </div>
          </aside>
        </div>
      </div>
      <Footer />
      <ChatBot />
      <CookieConsent />
    </div>
  );
}
